import { useCallback, useMemo } from 'react'
import { Scope, ContactListItem } from '@utils'
import { Contact, usePaginateContacts } from '@hooks'
import { useListingContactsStore } from '../store/useListingContactsStore'
import { UseListingContactsResult } from '../types'

// Hook principal pour le système listingContacts
export const useListingContacts = (navigation?: any): UseListingContactsResult => {
  // Store du système listingContacts (INDÉPENDANT)
  const {
    searchText,
    scope,
    localFilters,
    setSearchText,
    setScope,
    reset,
    hasLocalFilters,
    getActiveFiltersCount,
    getFilterSummary,
  } = useListingContactsStore()

  const hasGhost = scope.includes(Scope.GHOST)
  const hasPrivate = scope.includes(Scope.PRIVATE)
  const hasProfessional = scope.includes(Scope.PROFESSIONAL)
  const hasScopeFilter = hasPrivate || hasProfessional

  // Store global des contacts (personnes)
  const {
    flatData: contacts,
    isPending,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePaginateContacts({
    search: searchText,
    scope: hasPrivate ? Scope.PRIVATE : hasProfessional ? Scope.PROFESSIONAL : undefined,
    onlyGhost: hasGhost || undefined,
    noGhost: (hasScopeFilter && !hasGhost) || undefined,
    filters: {
      gender: localFilters.gender,
      ageRange: localFilters.ageRange,
      origins: (localFilters.origins || []).map((origin) => origin.id),
      residence: (localFilters.residence || []).map((residence) => residence.id),
      residenceContinents: (localFilters.residenceContinents || []).map((c) => c.id),
      residenceCountries: (localFilters.residenceCountries || []).map((c) => c.id),
      residenceRegions: (localFilters.residenceRegions || []).map((r) => r.id),
      residenceHistory: (localFilters.livedPlaces || []).map((livedPlace) => livedPlace.id),
      languages: (localFilters.languages || []).map((language) => language.id),
      nationalities: (localFilters.nationalities || []).map((nationality) => nationality.id),
      professions: (localFilters.professions || []).map((profession) => profession.id),
      categories: (localFilters.categories || []).map((category) => category.id),
      hashtags: (localFilters.hashtags || []).map((hashtag) => hashtag.id),
      meetingContextIds: (localFilters.contexts || []).map((c) => c.id),
      emotionalContextIds: (localFilters.emotionalContexts || []).map((c) => c.id),
      relationshipStatusIds: (localFilters.relationshipStatus || [])
        .map((s) => s.id)
        .filter(Boolean),
      relationalColorIds: (localFilters.relationalColor || []).map((r) => r.id),
    },
  })

  // Statistiques de filtrage
  const filterStats = useMemo(
    () => ({
      total: contacts?.length || 0,
      filtered: contacts?.length || 0,
      hasActiveFilters: hasLocalFilters() || searchText.trim().length > 0,
      totalFiltersCount: getActiveFiltersCount() + (searchText.trim().length > 0 ? 1 : 0),
    }),
    [contacts?.length, hasLocalFilters, searchText, getActiveFiltersCount],
  )

  // Actions
  const handleContactPress = useCallback((contact: Contact | ContactListItem) => {
    console.log('Contact sélectionné:', contact.fullName)
    // Navigation vers le profil du contact
    // navigate('ContactProfile', { contactId: contact.id })
  }, [])

  const handleFilterPress = useCallback(() => {
    if (navigation) {
      navigation.navigate('Contacts', { screen: 'ListingContactsFilters' })
    }
  }, [navigation])

  const handleReset = useCallback(() => {
    setSearchText('')
    reset()
  }, [setSearchText, reset])

  // Utilitaires
  const hasActiveFilters = useCallback(() => {
    return hasLocalFilters() || searchText.trim().length > 0
  }, [hasLocalFilters, searchText])

  const getActiveFiltersCountTotal = useCallback(() => {
    const baseCount = getActiveFiltersCount()
    const searchCount = searchText.trim().length > 0 ? 1 : 0
    return baseCount + searchCount
  }, [getActiveFiltersCount, searchText])

  const getFilterSummaryText = useCallback(() => {
    const parts: string[] = []

    // Ajouter la recherche
    if (searchText.trim().length > 0) {
      parts.push(`Recherche: "${searchText}"`)
    }

    // Ajouter le résumé des filtres
    const filterSummary = getFilterSummary()
    if (filterSummary) {
      parts.push(filterSummary)
    }

    return parts.length > 0 ? parts.join(', ') : 'Aucun filtre actif'
  }, [searchText, getFilterSummary])

  return {
    // Données
    contacts,

    // États
    searchText,
    scope,
    // isPending (not isFetching): background refetches and next-page loads must
    // keep the list mounted, otherwise it remounts and scrolls back to the top
    isPending,
    isFetchingNextPage,
    hasNextPage,
    // Statistiques
    filterStats,

    // Actions
    fetchNextPage,
    setSearchText,
    setScope,
    handleContactPress,
    handleFilterPress,
    handleReset,

    // Utilitaires
    hasActiveFilters,
    getActiveFiltersCount: getActiveFiltersCountTotal,
    getFilterSummary: getFilterSummaryText,
  }
}
